import { ReactNode } from "react";
import { makeStyles } from "tss-react/mui";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { Box, Typography } from "@mui/material";

const MAX_TABLE_WIDTH = 784;
const MAX_COLUMNS = 7;
const FIRST_COLUMN_WIDTH = 100 / 3;

export interface PricingSummaryTableProps {
  columns: string[];
  rows: ReactNode[][];
}

export function PricingSummaryTable({
  columns,
  rows,
}: Readonly<PricingSummaryTableProps>) {
  const { classes, cx } = useStyles();

  const visibleColumns = columns.slice(0, MAX_COLUMNS);
  const remainingColumnCount = Math.max(visibleColumns.length - 1, 1);
  const remainingColumnWidth =
    (100 - FIRST_COLUMN_WIDTH) / remainingColumnCount;

  const getColumnWidth = (index: number) =>
    `${index === 0 ? FIRST_COLUMN_WIDTH : remainingColumnWidth}%`;

  return (
    <Box className={classes.table}>
      <Box className={cx(classes.row, classes.headerRow)}>
        {visibleColumns.map((column, index) => (
          <Box
            key={index + column}
            className={cx(
              classes.cell,
              index > 0 && classes.dividerCell,
              index > 0 && classes.rightAligned
            )}
            style={{ width: getColumnWidth(index) }}
          >
            <Typography
              variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
              className={classes.headerText}
            >
              {column}
            </Typography>
          </Box>
        ))}
      </Box>
      {rows.map((row, rowIndex) => (
        <Box
          key={`row-${row.map(String).join("|")}`}
          className={cx(classes.row, classes.bodyRow)}
        >
          {visibleColumns.map((_, index) => (
            <Box
              key={index + rowIndex}
              className={cx(
                classes.cell,
                index > 0 && classes.dividerCell,
                index > 0 && classes.rightAligned
              )}
              style={{ width: getColumnWidth(index) }}
            >
              <Typography variant={TYPOGRAPHY_VARIANTS.BODY2}>
                {row[index]}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  table: {
    width: "100%",
    maxWidth: MAX_TABLE_WIDTH,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
  },
  row: {
    display: "flex",
    width: "100%",
    "&:not(:last-of-type)": {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  headerRow: {
    backgroundColor: theme.palette.grey[100],
  },
  bodyRow: {
    backgroundColor: theme.palette.common.white,
  },
  cell: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    padding: theme.spacing(1.5, 2),
  },
  dividerCell: {
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  rightAligned: {
    justifyContent: "flex-end",
    textAlign: "right",
  },
  headerText: {
    fontWeight: 500,
    lineHeight: 1.25,
    color: theme.palette.text.secondary,
  },
}));
